import type { AppModule, FirstInArgument } from '@mgCommon/types';
import { useJourneyBuilder } from '@opap/jbca-postmonger-react';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { lazy, useEffect, useMemo } from 'react';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import AppErrorMessage from './components/AppErrorMessage';
import AppLogo from './components/AppLogo';
import AppSpinner from './components/AppSpinner';
import ModuleLayout from './components/ModuleLayout';
import SelectAppModule from './components/SelectAppModule';
import AppToast from './components/ui/AppToast';
import { useInteractionTriggerEventDefinition } from './hooks/useInteractionTriggerEventDefinition';
import { useMcUserInfo } from './hooks/useMcUserInfo';
import { useCurrentModule } from './state';

function selectLazyComponent(module: AppModule | undefined) {
  switch (module) {
    case 'SMS':
      return lazy(() => import(/* webpackChunkName: "sms" */ '@sms/Sms'));
    case 'PUSH':
      return lazy(() => import(/* webpackChunkName: "webPush" */ '@webPush/WebPush'));
    case 'INTERSTITIAL':
      return lazy(() => import(/* webpackChunkName: "interstitial" */ '@interstitial/Interstitial'));
    case 'NATIVE_PUSH':
      return lazy(() => import(/* webpackChunkName: "nativePush" */ '@nativePush/NativePush'));
    case 'VIBER':
      return lazy(() => import(/* webpackChunkName: "viber" */ '@viber/Viber'));
    case 'IN_APP':
      return lazy(() => import(/* webpackChunkName: "inApp" */ '@inApp/InApp'));
    default:
      return null;
  }
}

export default function App() {
  const { activity } = useJourneyBuilder();
  const firstInArgument: FirstInArgument = activity.arguments.execute.inArguments[0];
  const messageType = firstInArgument?.messageType || undefined;
  const currentModule = useCurrentModule(state => state.currentModule);
  const setCurrentModule = useCurrentModule(state => state.setCurrentModule);

  const { isPending: isMcUserInfoPending, error: mcUserInfoError, userInfo } = useMcUserInfo();
  const { isPending: isEventDefinitionPending, error: eventDefinitionError } = useInteractionTriggerEventDefinition();

  const error = mcUserInfoError || eventDefinitionError;
  const isPending = isMcUserInfoPending || isEventDefinitionPending || (!!messageType && !currentModule);

  const LazyComponent = useMemo(() => selectLazyComponent(currentModule), [currentModule]);

  useEffect(() => {
    if (messageType) {
      setCurrentModule(messageType);
    }
  }, [messageType, setCurrentModule]);

  if (isPending) return <AppSpinner />;

  if (error || !userInfo) return <AppErrorMessage message={error?.message} />;

  return (
    <>
      <AppToast />
      <Container>
        <Row>
          <Col>
            <AppLogo />
          </Col>
        </Row>
        {LazyComponent ? (
          <ModuleLayout currentModule={currentModule}>
            <LazyComponent activityState={activity} />
          </ModuleLayout>
        ) : (
          <SelectAppModule currentModule={currentModule} userInfo={userInfo} />
        )}
      </Container>
      <ReactQueryDevtools initialIsOpen={false} />
    </>
  );
}
